import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { CarReservation, ItemPricing, useUpdateTripItem } from "@/hooks/useTrips";
import { toast } from "sonner";
import { PriceInputSection } from "../PriceInputSection";

const carSchema = z.object({
  rental_company: z.string().min(1, "Rental company is required"),
  car_type: z.string().optional(),
  // Pickup
  pickup_location: z.string().optional(),
  pickup_date: z.string().optional(),
  pickup_time: z.string().optional(),
  // Dropoff
  dropoff_location: z.string().optional(),
  dropoff_date: z.string().optional(),
  dropoff_time: z.string().optional(),
  confirmation_number: z.string().optional(),
});

type CarFormData = z.infer<typeof carSchema>;

interface EditCarDialogProps {
  car: CarReservation;
  tripId: string;
  index: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const EditCarDialog = ({
  car,
  tripId,
  index,
  open,
  onOpenChange,
}: EditCarDialogProps) => {
  const updateItem = useUpdateTripItem();
  const [pricing, setPricing] = useState<ItemPricing | undefined>(car.pricing);

  const form = useForm<CarFormData>({
    resolver: zodResolver(carSchema),
    defaultValues: {
      rental_company: car.rental_company || "",
      car_type: car.car_type || "",
      pickup_location: car.pickup_location || "",
      pickup_date: car.pickup_date || "",
      pickup_time: car.pickup_time || "",
      dropoff_location: car.dropoff_location || "",
      dropoff_date: car.dropoff_date || "",
      dropoff_time: car.dropoff_time || "",
      confirmation_number: car.confirmation_number || "",
    },
  });

  const onSubmit = (data: CarFormData) => {
    const updatedCar: CarReservation = {
      ...car,
      ...data,
      pricing,
    };

    updateItem.mutate(
      {
        tripId,
        itemType: "cars",
        index,
        item: updatedCar,
      },
      {
        onSuccess: () => {
          toast.success("Car rental updated");
          onOpenChange(false);
        },
        onError: (error) => {
          toast.error(`Failed to update: ${error.message}`);
        },
      }
    );
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Edit Car Rental</SheetTitle>
        </SheetHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 mt-6">
            {/* Car Info Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground">Rental Information</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="rental_company"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Rental Company</FormLabel>
                      <FormControl>
                        <Input placeholder="Hertz" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="car_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Car Type</FormLabel>
                      <FormControl>
                        <Input placeholder="Compact SUV" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="confirmation_number"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Confirmation #</FormLabel>
                    <FormControl>
                      <Input placeholder="ABC123" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <Separator />

            {/* Pickup Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground">Pick-up</h3>
              <FormField
                control={form.control}
                name="pickup_location"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Location</FormLabel>
                    <FormControl>
                      <Input placeholder="Airport Terminal 1" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="pickup_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Date</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="pickup_time"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Time</FormLabel>
                      <FormControl>
                        <Input type="time" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <Separator />

            {/* Dropoff Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground">Drop-off</h3>
              <FormField
                control={form.control}
                name="dropoff_location"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Location</FormLabel>
                    <FormControl>
                      <Input placeholder="Airport Terminal 1" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="dropoff_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Date</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="dropoff_time"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Time</FormLabel>
                      <FormControl>
                        <Input type="time" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <Separator />

            {/* Pricing Section */}
            <PriceInputSection
              pricing={pricing}
              onChange={setPricing}
            />

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={updateItem.isPending}>
                {updateItem.isPending ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
};
